import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, GripVertical, Loader2, Save } from 'lucide-react';
import { primaryBtnClass, secondaryBtnClass } from '../styles';
import { useRefreshSubtabs } from '../subtabs';
import {
  fetchAllAdminSubtabs,
  fetchAllAdminTabs,
  reorderAdminSubtabs,
  reorderAdminTabs,
} from './api';
import type { AdminSubtabRow, AdminTabRow } from './types';

const HIDDEN_TAB_KEYS = new Set(['tiers', 'prompts']);

const TabsOrderingPanel: React.FC = () => {
  const refreshSubtabs = useRefreshSubtabs();
  const [tabs, setTabs] = useState<AdminTabRow[]>([]);
  const [subtabs, setSubtabs] = useState<AdminSubtabRow[]>([]);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [tabRows, subRows] = await Promise.all([
        fetchAllAdminTabs(),
        fetchAllAdminSubtabs(),
      ]);
      const visibleTabs = tabRows
        .filter((t) => !HIDDEN_TAB_KEYS.has(t.key))
        .sort((a, b) => a.display_order - b.display_order || a.id - b.id);
      setTabs(visibleTabs);
      setSubtabs(
        subRows
          .slice()
          .sort(
            (a, b) =>
              a.tab - b.tab ||
              a.display_order - b.display_order ||
              a.id - b.id
          )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tabs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const moveTab = (index: number, direction: -1 | 1) => {
    setTabs((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next.map((tab, i) => ({ ...tab, display_order: i }));
    });
  };

  const moveSubtab = (tabId: number, index: number, direction: -1 | 1) => {
    setSubtabs((prev) => {
      const group = prev
        .filter((s) => s.tab === tabId)
        .sort((a, b) => a.display_order - b.display_order || a.id - b.id);
      const target = index + direction;
      if (target < 0 || target >= group.length) return prev;
      const swapped = [...group];
      const tmp = swapped[index];
      swapped[index] = swapped[target];
      swapped[target] = tmp;
      const reindexed = swapped.map((row, i) => ({ ...row, display_order: i }));
      const others = prev.filter((s) => s.tab !== tabId);
      return [...others, ...reindexed].sort(
        (a, b) => a.tab - b.tab || a.display_order - b.display_order || a.id - b.id
      );
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await Promise.all([
        reorderAdminTabs(
          tabs.map((tab, index) => ({
            id: tab.id,
            display_order: index,
            label: tab.label,
            is_visible: tab.is_visible,
          }))
        ),
        reorderAdminSubtabs(
          subtabs.map((row) => ({
            id: row.id,
            display_order: row.display_order,
            label: row.label,
            is_visible: row.is_visible,
          }))
        ),
      ]);
      await Promise.all([load(), refreshSubtabs()]);
      setMessage('Tab and subtab order saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save ordering');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-8">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {(error || message) && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-800'
          }`}
        >
          {error || message}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[13px] text-[#626262]">
          Reorder, rename, or hide tabs and their subtabs. New tab types still require a code
          component.
        </p>
        <button
          type="button"
          disabled={saving}
          onClick={() => void save()}
          className={`${primaryBtnClass} inline-flex items-center gap-1.5`}
        >
          <Save className="w-3.5 h-3.5" />
          Save
        </button>
      </div>
      <ul className="space-y-2">
        {tabs.map((tab, index) => {
          const group = subtabs
            .filter((s) => s.tab === tab.id)
            .sort((a, b) => a.display_order - b.display_order || a.id - b.id);
          const isOpen = Boolean(expanded[tab.id]);
          return (
            <li key={tab.id} className="border border-[#E8E8E8] rounded-lg overflow-hidden">
              <div className="flex items-center gap-2 px-2 py-2 bg-white">
                <button
                  type="button"
                  className="p-1 text-[#8A8A8A]"
                  onClick={() =>
                    setExpanded((prev) => ({ ...prev, [tab.id]: !prev[tab.id] }))
                  }
                  aria-label={isOpen ? 'Collapse subtabs' : 'Expand subtabs'}
                >
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                <GripVertical className="w-4 h-4 text-[#B0B0B0]" />
                <input
                  value={tab.label}
                  onChange={(e) =>
                    setTabs((prev) =>
                      prev.map((t) =>
                        t.id === tab.id ? { ...t, label: e.target.value } : t
                      )
                    )
                  }
                  className="flex-1 h-8 rounded border border-[#D1D1D1] px-2 text-sm"
                />
                <span className="text-[11px] text-[#8A8A8A] font-mono">{tab.key}</span>
                <label className="text-[12px] text-[#4A4A4A] inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={tab.is_visible}
                    onChange={(e) =>
                      setTabs((prev) =>
                        prev.map((t) =>
                          t.id === tab.id ? { ...t, is_visible: e.target.checked } : t
                        )
                      )
                    }
                  />
                  Visible
                </label>
                <button
                  type="button"
                  className={`${secondaryBtnClass} !h-8 !px-2 text-[12px]`}
                  disabled={index === 0}
                  onClick={() => moveTab(index, -1)}
                >
                  Up
                </button>
                <button
                  type="button"
                  className={`${secondaryBtnClass} !h-8 !px-2 text-[12px]`}
                  disabled={index === tabs.length - 1}
                  onClick={() => moveTab(index, 1)}
                >
                  Down
                </button>
              </div>
              {isOpen && (
                <ul className="border-t border-[#F0F0F0] bg-[#FAFAFA] px-3 py-2 space-y-2">
                  {group.length === 0 && (
                    <li className="text-[12px] text-[#8A8A8A] px-1 py-1">No subtabs</li>
                  )}
                  {group.map((sub, subIndex) => (
                    <li
                      key={sub.id}
                      className="flex items-center gap-2 border border-[#E8E8E8] rounded-md bg-white px-2 py-1.5"
                    >
                      <GripVertical className="w-3.5 h-3.5 text-[#B0B0B0]" />
                      <input
                        value={sub.label}
                        onChange={(e) =>
                          setSubtabs((prev) =>
                            prev.map((s) =>
                              s.id === sub.id ? { ...s, label: e.target.value } : s
                            )
                          )
                        }
                        className="flex-1 h-8 rounded border border-[#D1D1D1] px-2 text-sm"
                      />
                      <span className="text-[11px] text-[#8A8A8A] font-mono">{sub.key}</span>
                      <label className="text-[12px] text-[#4A4A4A] inline-flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={sub.is_visible}
                          onChange={(e) =>
                            setSubtabs((prev) =>
                              prev.map((s) =>
                                s.id === sub.id
                                  ? { ...s, is_visible: e.target.checked }
                                  : s
                              )
                            )
                          }
                        />
                        Visible
                      </label>
                      <button
                        type="button"
                        className={`${secondaryBtnClass} !h-8 !px-2 text-[12px]`}
                        disabled={subIndex === 0}
                        onClick={() => moveSubtab(tab.id, subIndex, -1)}
                      >
                        Up
                      </button>
                      <button
                        type="button"
                        className={`${secondaryBtnClass} !h-8 !px-2 text-[12px]`}
                        disabled={subIndex === group.length - 1}
                        onClick={() => moveSubtab(tab.id, subIndex, 1)}
                      >
                        Down
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default TabsOrderingPanel;
